<template>
  <v-dialog persistent max-width="400" v-model="dialog">
    <v-card>
      <v-card-title class="text-h5">Autenticación de dos factores</v-card-title>
      <v-card-text class="text-center">
        <div v-if="!showVerification">
          <p class="mb-4">Escanea el código QR con tu aplicación de autenticación</p>
          
          <div class="qr-container d-flex justify-center align-center mb-4">
            <canvas 
              ref="qrCanvas"
              v-show="qrUrl"
              width="200"
              height="200"
            ></canvas>
            <v-progress-circular
              v-show="!qrUrl"
              indeterminate
              color="primary"
            ></v-progress-circular>
          </div>

          <div class="d-flex flex-column gap-3">
            <v-btn
              v-bind="defaultButtonProps"
              color="primary"
              block
              @click="startVerification"
              :disabled="!qrUrl"
              class="mb-3"
            >
              Continuar
            </v-btn>

            <v-btn
              v-bind="defaultButtonProps"
              block
              color="error"
              @click="handleCancel"
            >
              Cancelar
            </v-btn>
          </div>

          <p class="text-body-2 grey--text mt-4">
            Si no puedes escanear el código QR, contacta con soporte técnico
          </p>
        </div>

        <div v-else>
          <p class="mb-4">Ingresa el código de verificación de tu aplicación</p>
          
          <v-otp-input
            v-model="verificationCode"
            :length="6"
            :loading="loading"
            class="mb-4"
            @finish="verifyCode"
          ></v-otp-input>

          <v-alert
            v-if="error"
            type="error"
            dense
            class="mb-4"
          >
            {{ error }}
          </v-alert>
        </div>
      </v-card-text>
      <v-card-actions>
        <template v-if="showVerification">
          <v-btn
            v-bind="defaultButtonProps"
            color="error"
            @click="handleCancel"
          >
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            v-if="isFirstTimeSetup"
            v-bind="defaultButtonProps"
            @click="showVerification = false"
          >
            Volver
          </v-btn>
          <v-btn
            v-bind="defaultButtonProps"
            color="primary"
            @click="verifyCode"
            :loading="loading"
            :disabled="!verificationCode || verificationCode.length !== 6"
          >
            Verificar
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import QRCode from 'qrcode'

export default {
  name: 'MAFDialog',

  props: {
    token: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      dialog: false,
      qrUrl: '',
      error: null,
      loading: false,
      showVerification: false,
      verificationCode: '',
      isFirstTimeSetup: false,
    }
  },

  computed: {
    defaultButtonProps() {
      return {
        rounded: true,
        disabled: this.loading
      }
    }
  },

  watch: {
    qrUrl: {
      immediate: true,
      handler(newUrl) {
        if (newUrl) {
          this.$nextTick(() => {
            this.generateQR(newUrl)
          })
        }
      }
    },
    showVerification(newValue) {
      if (!newValue && this.qrUrl) {
        this.$nextTick(() => {
          this.generateQR(this.qrUrl)
        })
      }
    }
  },

  methods: {
    open(isFirstTime = false, qrUri = '') {
      this.dialog = true;
      this.isFirstTimeSetup = isFirstTime;
      this.showVerification = !isFirstTime;
      this.verificationCode = '';
      this.error = null;
      if (isFirstTime && qrUri) {
        this.qrUrl = qrUri;
      }
    },

    close() {
      this.dialog = false;
      this.qrUrl = '';
      this.error = null;
      this.showVerification = false;
      this.verificationCode = '';
      this.isFirstTimeSetup = false;
    },

    handleCancel() {
      this.$emit('cancelled');
      this.close();
    },

    async generateQR(text) {
      try {
        const canvas = this.$refs.qrCanvas;
        if (canvas) {
          await QRCode.toCanvas(canvas, text, {
            width: 200,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#ffffff'
            }
          });
        }
      } catch (err) {
        console.error('Error generating QR code:', err);
        this.error = 'Error al generar el código QR';
      }
    },

    startVerification() {
      this.showVerification = true;
      this.error = null;
    },

    async verifyCode() {
      if (!this.verificationCode || this.verificationCode.length !== 6) {
        return;
      }

      try {
        this.loading = true;
        this.error = null;
        
        const response = await this.$store.dispatch('validateTOTP', {
          token: this.token,
          code: this.verificationCode
        });

        if (!response.success) {
          this.error = response.error || 'Código inválido. Por favor, intenta de nuevo.';
          return;
        }
        
        this.$emit('verified');
        this.close();
        
      } catch (err) {
        this.error = 'Error al validar el código. Por favor, intenta de nuevo.';
        console.error('Error verifying code:', err);
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.qr-container {
  min-height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

canvas {
  background: white;
  border-radius: 8px;
  padding: 8px;
}

.gap-3 {
  gap: 12px;
}

/* Estilos para el OTP input */
:deep(.v-otp-input) {
  display: flex;
  justify-content: center;
  gap: 8px;
}

:deep(.v-otp-input__field) {
  width: 40px !important;
  height: 40px !important;
  font-size: 1.2em !important;
  text-align: center;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

:deep(.v-otp-input__field:focus) {
  border-color: var(--v-primary-base);
  box-shadow: 0 0 0 2px rgba(var(--v-primary-base), 0.2);
}
</style>